import { FroalaInput } from '@app/react-common';
import { DatePicker, TimePicker } from '@app/react-common/Pickers';
import type { FormInstance } from '@brokerbay/elements-web';
import {
  Col,
  Color,
  Form,
  Input as OldInput,
  Row,
  Switch as OldSwitch,
} from '@brokerbay/elements-web';
import {
  Input as NewInput,
  Switch as NewSwitch,
} from '@bb/elements-web-rebranding';
import moment, { Moment } from 'moment';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { featureEnabled } from 'utils';

// Localizer for DateTimePicker
moment.locale('en');

interface AnnouncementEditFormProps {
  form: FormInstance;
}

const AnnouncementEditForm: React.FC<AnnouncementEditFormProps> = ({
  form,
}) => {
  const sendLater = Form.useWatch('sendLater', form);
  const delay = Form.useWatch('delay', form);

  const [datePickerValue, setDatePickerValue] = useState('');
  const [timePickerValue, setTimePickerValue] = useState('');

  const hasSupraRebrandingFeature = useMemo(
    () => featureEnabled('supraRebrandingFeature', 1),
    [],
  );

  const Input = useMemo(
    () => (hasSupraRebrandingFeature ? NewInput : OldInput),
    [hasSupraRebrandingFeature],
  );

  const Switch = useMemo(
    () => (hasSupraRebrandingFeature ? NewSwitch : OldSwitch),
    [hasSupraRebrandingFeature],
  );

  const onDateChange = (date: Moment, value: string) => {
    const day = moment(date).dayOfYear();
    const newDelay = moment(date).dayOfYear(day).seconds(0).toDate();

    setDatePickerValue(value);
    form.setFieldsValue({ delay: newDelay });
  };

  const onTimeChange = (time: Moment, value: string) => {
    const currentDelay = form.getFieldValue('delay') || new Date();
    const hour = moment(time).hours();
    const min = moment(time).minutes();
    const newDelay = moment(currentDelay)
      .hours(hour)
      .minutes(min)
      .seconds(0)
      .toDate();

    setTimePickerValue(value);
    form.setFieldsValue({ delay: newDelay });
  };

  return (
    <Form dataCy="announcement-edit-form" layout="vertical" form={form as any}>
      <Row gutter={[32, 16]}>
        <Col xs={24} md={12} xl={11}>
          <Form.Item label="Reply-To:" name="replyTo">
            <Input dataCy="reply-to-input" />
          </Form.Item>
          <StyledFormItemSubject label="Subject:" name="subject">
            <Input dataCy="subject-input" />
          </StyledFormItemSubject>
        </Col>
        <Col xs={24} md={12} xl={{ span: 10, offset: 1 }}>
          <StyledFormItemSendLater
            label="Schedule - Send Later:"
            name="sendLater"
            valuePropName="checked"
          >
            <Switch dataCy="send-later-switch" />
          </StyledFormItemSendLater>

          {sendLater && (
            <Form.Item label="Send On:">
              <StyledDateTimeRow gutter={[8, 0]}>
                <Col span={13} xxl={9}>
                  <DatePicker
                    fullWidth
                    inputValue={datePickerValue}
                    inputVariant="outlined"
                    value={delay}
                    onChange={onDateChange}
                    format="YYYY-MM-DD"
                    variant="inline"
                    autoOk
                    disablePast
                  />
                </Col>
                <Col span={11} xxl={9}>
                  <TimePicker
                    fullWidth
                    inputValue={timePickerValue}
                    value={delay}
                    onChange={onTimeChange}
                    inputVariant="outlined"
                    mask="__:__ _M"
                    variant="inline"
                    autoOk
                  />
                </Col>
              </StyledDateTimeRow>
            </Form.Item>
          )}
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <StyledFroalaContainer>
            <Form.Item
              name="body"
              valuePropName="model"
              trigger="onModelChange"
            >
              <FroalaInput
                config={{
                  pluginsEnabled: ['url'],
                  toolbarButtons: {
                    moreRich: {
                      buttons: [
                        'insertLink',
                        'insertImage',
                        'insertFile',
                        'insertVideo',
                        'insertHR',
                      ],
                    },
                  },
                }}
              />
            </Form.Item>
          </StyledFroalaContainer>
        </Col>
      </Row>

      {/* Hidden field to store delay */}
      <Form.Item name="delay" hidden>
        <Input dataCy="hidden-delay-input" />
      </Form.Item>
    </Form>
  );
};

export default AnnouncementEditForm;

const StyledFormItemSubject = styled(Form.Item)`
  margin-bottom: 27px;
`;

const StyledFormItemSendLater = styled(Form.Item)`
  margin-bottom: 27px;

  && .ant-switch,
  && .ant5-switch {
    max-width: 50px;
  }
`;

const StyledDateTimeRow = styled(Row)`
  &&& .MuiInputBase-root {
    border-radius: 8px;
    border-color: ${Color.TRUNKS};
  }

  &&& input.MuiInputBase-input {
    padding-right: 0;
  }
`;

const StyledFroalaContainer = styled.div`
  margin-top: 20px;
`;
